import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SSEHeartbeatEvent, SSETokenEvent, SSECompleteEvent, SSEErrorEvent, SSEStatusEvent, DEOAgentMessageRequest, DEOAgentAPIResponse } from '../models/chat.models';
import { HeaderBuilderService } from 'app/shared/services/header-builder/header-builder.service';
import { stripSupportReferenceCode } from './api-error.helper';

class SseHttpError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export type SSEEvent = SSEHeartbeatEvent | SSETokenEvent | SSECompleteEvent | SSEErrorEvent | SSEStatusEvent;

/** The progress fields of a `status` event (DATA-1629); a field of another type is dropped rather than passed on. */
const PROGRESS_FIELD_TYPES: Record<string, 'number' | 'string' | 'boolean'> = {
  step: 'number',
  text: 'string',
  kind: 'string',
  label: 'string',
  index: 'number',
  total: 'number',
  ok: 'boolean',
  duration_ms: 'number'
};

@Injectable({
  providedIn: 'root'
})
export class SseClientService {
  constructor(private readonly _headerBuilder: HeaderBuilderService) {}

  /**
   * Send a message and receive SSE stream with heartbeat, complete, and error events.
   *
   * Unsubscribing from the returned Observable aborts the in-flight request, allowing
   * callers (e.g. a "Stop" button) to cancel a response that is taking too long.
   */
  sendMessageWithSSE(url: string, request: DEOAgentMessageRequest): Observable<SSEEvent> {
    return new Observable<SSEEvent>(subscriber => {
      const abortController = new AbortController();

      // Use fetch API for SSE streaming since Angular HttpClient doesn't support SSE natively
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...this._headerBuilder.buildHeaders()
      };

      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: abortController.signal
      })
        .then(async response => {
          if (!response.ok) {
            // DATA-1351: pre-stream failures (e.g. empty message → 400) return the JSON envelope instead of
            // an SSE stream; its message is user-friendly and carries the support reference code.
            throw new SseHttpError(await this.readErrorEnvelopeMessage(response), response.status);
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('Response body is null');
          }

          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              subscriber.complete();
              break;
            }

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE messages
            const messages = buffer.split('\n\n');
            buffer = messages.pop() || ''; // Keep incomplete message in buffer

            for (const message of messages) {
              if (!message.trim()) continue;

              try {
                const event = this.parseSSEMessage(message);
                if (event) {
                  subscriber.next(event);

                  // Complete the stream on complete or error events
                  if (event.type === 'complete' || event.type === 'error') {
                    subscriber.complete();
                    return;
                  }
                }
              } catch (error) {
                console.error('Error parsing SSE message:', error, message);
              }
            }
          }
        })
        .catch(error => {
          // The request was cancelled by the caller (Stop button / unsubscribe) — not a real error.
          if (abortController.signal.aborted) {
            return;
          }

          console.error('SSE connection error:', error);
          subscriber.next({
            type: 'error',
            error: error.message || 'Connection failed',
            ...(error instanceof SseHttpError ? { status: error.status } : {})
          });
          subscriber.complete();
        });

      // Teardown: aborts the fetch when the caller unsubscribes (e.g. user stops the response)
      return () => abortController.abort();
    });
  }

  /**
   * Parse SSE message format
   * Format:
   *   event: heartbeat
   *   data: {"progress": "Processing..."}
   *
   *   event: status
   *   data: {"phase": "generating_artifact", "artifact_bytes": 4821, "session_id": "..."}
   *
   *   event: complete
   *   data: {"content": "...", "usage": {...}, "artifacts": [...], "session_id": "..."}
   *
   *   event: error
   *   data: {"error": "Error message", "error_ref": "a1b2c3d4"}   (error_ref optional)
   */
  private parseSSEMessage(message: string): SSEEvent | null {
    const lines = message.split('\n');
    let eventType = 'message'; // Default event type
    let data = '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        data = line.substring(5).trim();
      }
    }

    if (!data) return null;

    try {
      const parsedData = JSON.parse(data);

      switch (eventType) {
        case 'heartbeat':
          return {
            type: 'heartbeat',
            progress: parsedData.progress
          };

        case 'token':
          return {
            type: 'token',
            delta: parsedData.delta ?? parsedData.content ?? '',
            session_id: parsedData.session_id ?? ''
          };

        case 'status':
          return this.parseStatusEvent(parsedData);

        case 'complete':
          return {
            type: 'complete',
            data: parsedData as DEOAgentAPIResponse
          };

        case 'error':
          return {
            type: 'error',
            error: this.buildErrorEventText(parsedData)
          };

        default:
          console.warn('Unknown SSE event type:', eventType);
          return null;
      }
    } catch (error) {
      console.error('Failed to parse SSE data:', error, data);
      return null;
    }
  }

  /**
   * Reports what the backend is doing while the turn runs, so tens of seconds of legitimate model work
   * do not look like a hang (DATA-1410, DATA-1629). Consumers ignore phases they do not know.
   */
  private parseStatusEvent(parsedData: Record<string, unknown>): SSEStatusEvent {
    const event: SSEStatusEvent = {
      type: 'status',
      phase: typeof parsedData['phase'] === 'string' ? parsedData['phase'] : '',
      artifact_bytes: parsedData['artifact_bytes'] as number | undefined,
      session_id: parsedData['session_id'] as string | undefined
    };
    for (const [field, type] of Object.entries(PROGRESS_FIELD_TYPES)) {
      const value = parsedData[field];
      if (typeof value === type) {
        (event as unknown as Record<string, unknown>)[field] = value;
      }
    }
    return event;
  }

  /**
   * Error events may carry a separate error_ref (DATA-1351). PLAN-2314 keeps reference codes out of what
   * the user reads, so error_ref is deliberately not appended and any code the message already embeds is
   * stripped; the code still reaches support through the response body and the backend logs.
   */
  private buildErrorEventText(parsedData: { error?: string; message?: string; error_ref?: string }): string {
    const text = parsedData.error || parsedData.message || 'Unknown error';
    return stripSupportReferenceCode(text) || 'Unknown error';
  }

  /** Reads the {data, message, succeeded} envelope of a non-2xx response, with a fallback for non-envelope bodies. */
  private async readErrorEnvelopeMessage(response: Response): Promise<string> {
    const fallback = `The request failed (HTTP ${response.status}). Please try again.`;
    try {
      const envelope = await response.json();
      const message = typeof envelope?.message === 'string' ? stripSupportReferenceCode(envelope.message) : '';
      return message.trim() ? message : fallback;
    } catch {
      return fallback;
    }
  }
}
